import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { useMapLayers } from '../data/geo.ts'
import { formatLocal } from '../lib/time.ts'
import { BackLink, Banner, DisabledHint, Field, PendingBanner } from '../ui/components.tsx'
import { fileSize } from './MapPage.tsx'
import { useWorkshopAction, useWriteAccess } from '../ui/hooks.ts'

interface Manifest { sha256: string; bytes: number; source: string; attribution: string; capture_year: number | null; resolution_m: number | null; min_zoom: number; max_zoom: number; bounds: number[] | null; tile_count?: number }
const SOURCE_NAMES: Record<string, string> = { ign: 'IGN BD ORTHO', synthetic: 'Fond de test (fictif)' }

function readManifest(text: string): Manifest | null {
  try {
    const m = JSON.parse(text) as Record<string, unknown>
    const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null)
    if (typeof m.sha256 !== 'string' || !/^[0-9a-f]{64}$/.test(m.sha256) || num(m.bytes) === null || num(m.min_zoom) === null || num(m.max_zoom) === null || typeof m.attribution !== 'string' || !m.attribution) return null
    const b = Array.isArray(m.bounds) && m.bounds.length === 4 && m.bounds.every((x) => num(x) !== null) ? (m.bounds as number[]) : null
    const source = typeof m.source === 'string' ? SOURCE_NAMES[m.source] ?? m.source : 'carte'
    return { sha256: m.sha256, bytes: m.bytes as number, source: source.slice(0, 60), attribution: m.attribution.slice(0, 200), capture_year: num(m.capture_year), resolution_m: num(m.resolution_m), min_zoom: m.min_zoom as number, max_zoom: m.max_zoom as number, bounds: b, tile_count: num(m.tile_count) ?? undefined }
  } catch { return null }
}

/** Publish a map archive that the operator has built (ops/tiles) and uploaded to the private bucket. The server checks the file is really there with
 *  the announced size before it records the layer; devices then download it. */
export function MapPublishPage() {
  const { t, i18n } = useTranslation()
  const { org, site } = useClub()
  const publish = useWriteAccess('map.publish')
  const layers = useMapLayers()
  const w = useWorkshopAction()
  const id = useRef(newId())
  const [manifest, setManifest] = useState<Manifest | null>(null)
  const [bad, setBad] = useState(false)
  const [note, setNote] = useState('')
  const key = manifest ? `org/${org.org_id}/site/${site.site_id}/${manifest.sha256}.pmtiles` : ''

  const onFile = async (f: File | undefined) => {
    setBad(false); setManifest(null)
    if (!f) return
    const m = readManifest(await f.text())
    if (m) setManifest(m); else setBad(true)
  }
  const go = async () => {
    if (!manifest) return
    const ok = await w.run('map_layer.publish@1', {
      layer_id: id.current, kind: 'ortho_pmtiles', storage_key: key, sha256: manifest.sha256, bytes: manifest.bytes, source: manifest.source, attribution: manifest.attribution,
      min_zoom: manifest.min_zoom, max_zoom: manifest.max_zoom, ...(manifest.capture_year ? { capture_year: manifest.capture_year } : {}),
      ...(manifest.resolution_m ? { resolution_m: manifest.resolution_m } : {}), ...(manifest.bounds ? { bounds: manifest.bounds } : {}), ...(note.trim() ? { note: note.trim() } : {}),
    }, 'mapPublish.done')
    if (ok) { id.current = newId(); setManifest(null); setNote('') }
  }

  return (
    <section className="flex flex-col gap-4" data-testid="map-publish-page">
      <BackLink to="/map">← {t('common.back')}</BackLink>
      <h1 className="text-2xl font-bold">{t('mapPublish.title')}</h1>
      <p className="meta">{t('mapPublish.help')}</p>
      <ol className="list-decimal pl-5 text-sm opacity-90">
        <li>{t('mapPublish.step1')}</li><li>{t('mapPublish.step2')}</li><li>{t('mapPublish.step3')}</li>
      </ol>
      {!publish.permitted && <Banner tone="warn">{t('mapPublish.notAllowed')}</Banner>}

      <h2 className="text-lg font-bold">{t('mapPublish.published')}</h2>
      <ul className="flex flex-col gap-2" data-testid="layer-list">
        {(layers ?? []).length === 0 && <li className="opacity-70">{t('map.noLayer')}</li>}
        {[...(layers ?? [])].sort((a, b) => b.version - a.version).map((l) => (
          <li key={l.id} className="card text-sm" data-testid="layer-row">
            <strong>{t('mapPublish.version', { n: l.version })}</strong> · {l.attribution} · {fileSize(l.bytes, i18n.language)} · {formatLocal(l.created_at, site.timezone, 'dd/MM/yyyy HH:mm')}
            <br /><span className="opacity-70">z{l.min_zoom}–{l.max_zoom} · {l.sha256.slice(0, 12)}…</span>
          </li>
        ))}
      </ul>

      {publish.allowed && (
        <div className="card flex flex-col gap-3">
          <h2 className="text-lg font-bold">{t('mapPublish.new')}</h2>
          <Field label={t('mapPublish.manifest')} hint={t('mapPublish.manifestHelp')} htmlFor="map-manifest">
            <input id="map-manifest" type="file" accept="application/json,.json" className="input" onChange={(e) => void onFile(e.target.files?.[0])} data-testid="map-manifest" />
          </Field>
          {bad && <Banner tone="error"><span data-testid="manifest-bad">{t('mapPublish.badManifest')}</span></Banner>}
          {manifest && (
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm" data-testid="manifest-summary">
              <dt className="font-semibold">{t('mapPublish.attribution')}</dt><dd>{manifest.attribution}</dd>
              <dt className="font-semibold">{t('mapPublish.size')}</dt><dd>{fileSize(manifest.bytes, i18n.language)}</dd>
              <dt className="font-semibold">{t('mapPublish.zooms')}</dt><dd>{manifest.min_zoom}–{manifest.max_zoom}</dd>
              <dt className="font-semibold">{t('mapPublish.hash')}</dt><dd className="break-all font-mono text-xs">{manifest.sha256}</dd>
            </dl>
          )}
          <Field label={t('mapPublish.note')} htmlFor="map-note"><input id="map-note" className="input" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} data-testid="map-note" /></Field>
          {w.pending && <PendingBanner />}
          {w.error && <Banner tone="error"><span data-testid="publish-error">{w.error}</span></Banner>}
          {w.ok && <Banner tone="ok"><span data-testid="publish-ok">{w.ok}</span></Banner>}
          <DisabledHint show={!manifest}>{t('mapPublish.pickManifest')}</DisabledHint>
          <button className="btn btn-primary" disabled={!manifest || w.busy} onClick={() => void go()} data-testid="map-publish-go">{t('mapPublish.go')}</button>
        </div>
      )}
    </section>
  )
}
